import { AppContextService } from './app-context.service';
import { Optional } from '@angular/core';
import { Subscription } from 'rxjs';
import { LocalStorageService } from './local-storage.service';
import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { WorkspaceOpenMode } from './workspace-route';

type WorkspaceMode = 'windowed' | 'maximized' | 'docked-right';

const WORKSPACE_MODE_PREFIX = 'ws-routed-card-mode@2:';

/**
 * The viewing preference for the active routed Workspace.
 *
 * The App Shell still owns the one Workspace frame and its controls, but each
 * route remembers its own windowed/maximized/docked choice. Record ids are
 * collapsed in `workspaceLayoutKey`, so the preference belongs to a screen,
 * not to whichever record happens to be open in it.
 *
 * What a page does get to affect is how the Workspace opens BEFORE anyone has
 * chosen. Territories and Sales Route draw on the shared map - tracing a
 * boundary, reading a driving order - and windowed puts an opaque card and a
 * dimming backdrop over the whole of it, so those pages opened with their
 * subject invisible. The route table says which pages those are (see
 * `mapPage` in app-routing.module.ts); the shell passes that fact here. It is
 * a default, not a mode: one explicit toggle on that route decides the rest of
 * the visit.
 *
 * On those two pages it decides ONLY the visit. Everywhere else the choice is
 * stored and wins whenever the route is opened again — see `remember`.
 */
@Injectable({ providedIn: 'root' })
export class WorkspaceLayoutService implements OnDestroy {
  private readonly modeSubject = new Subject<WorkspaceMode>();
  readonly modeChanged$ = this.modeSubject.asObservable();

  private readonly sessionSubscription;
  private contextRegistration?: Subscription;
  private contextReset?: Subscription;
  constructor(private readonly storage: LocalStorageService,
    @Optional() private readonly context?: AppContextService,
  ) {
    this.registerContext();
    this.contextReset = context?.sessionChanged$.subscribe(() => this.registerContext());
    this.sessionSubscription = storage.sessionChanged$.subscribe(() => {
      this.chosen = null;
      this.stored = readMode(this.storage, this.routeKey);
      this.publishMode();
    });
  }

  private registerContext(): void {
    this.contextRegistration?.unsubscribe();
    this.contextRegistration = this.context?.registerView({
      key: 'workspace-layout', name: 'Workspace presentation', priority: -50,
      read: () => this.mode,
      describe: mode => mode,
      restore: mode => {
        this.chosen = mode;
        this.publishMode();
      },
    });
  }

  ngOnDestroy(): void {
    this.contextRegistration?.unsubscribe();
    this.contextReset?.unsubscribe();
    this.sessionSubscription.unsubscribe();
    this.modeSubject.complete();
  }
  private routeKey = '';

  /** Null until the user picks one for the active route. */
  private chosen: WorkspaceMode | null = null;

  /** The active route's remembered choice, read once when the route changes. */
  private stored: WorkspaceMode | null = null;

  /** Whether the active route draws on the map. Not a preference. */
  private mapPage = false;

  /**
   * Selects the preference owned by the active route. A navigation reads that
   * route's stored choice but never writes one merely because it was opened —
   * and on a map page it does not get to answer at all (see `mode`).
   */
  setRoute(
    url: string,
    mapPage: boolean,
    openMode: WorkspaceOpenMode | null = null,
  ): void {
    const routeKey = workspaceLayoutKey(url);
    this.mapPage = mapPage;
    if (routeKey !== this.routeKey) {
      this.routeKey = routeKey;
      this.stored = readMode(this.storage, this.routeKey);
      this.chosen = null;
    }
    if (openMode !== null) this.chosen = openMode;
    this.publishMode();
  }

  get maximized(): boolean {
    return this.mode === 'maximized';
  }

  get dockedRight(): boolean {
    return this.mode === 'docked-right';
  }

  /** Applies an entry's opening intent to the current visit without saving it. */
  openAs(mode: WorkspaceOpenMode): void {
    this.chosen = mode;
    this.publishMode();
  }

  toggleMaximize(): void {
    this.chosen = this.maximized ? 'windowed' : 'maximized';
    this.remember();
    this.publishMode();
  }

  toggleDockRight(): void {
    this.chosen = this.dockedRight ? 'windowed' : 'docked-right';
    this.remember();
    this.publishMode();
  }

  private publishMode(): void {
    this.modeSubject.next(this.mode);
  }

  /**
   * Keep this choice for next time — unless the page is drawn on the map.
   *
   * Maximizing a map page covers the map completely, and one press then held
   * for every visit after it. Reported as "the map is not shown anymore",
   * which is exactly what it looks like from the outside: a page whose whole
   * subject is a boundary or a driving order, opening without it, days after
   * the button that did it was pressed.
   *
   * So on those pages the toggle lasts the visit and is not written. Maximize
   * to read the table, navigate away, come back to the map — which is the
   * thing the page is for. The cost is that somebody who genuinely prefers
   * sales-route maximized re-presses it each visit; the alternative is a
   * screen that silently stops showing what it is about.
   *
   * Every other page keeps its choice, because nothing there is hidden by it.
   */
  private remember(): void {
    if (this.mapPage || this.chosen === null) return;
    writeMode(this.storage, this.routeKey, this.chosen);
  }

  /**
   * `mapPage` is consulted BEFORE `stored`, not while reading it.
   *
   * The shell calls setRoute twice on a cold load. Once from ngOnInit, where
   * RouterModule.forRoot(routes) has not finished its enabledNonBlocking
   * initial navigation, so the route tree is not built and the page cannot yet
   * say it draws on the map - mapPage arrives false. Then again on
   * NavigationEnd, where it can, and where the same-route guard in setRoute
   * makes that call return early.
   *
   * Deciding at read time therefore let the boot call's answer stand for the
   * whole visit: a hard reload or a pasted URL at /leads/territories opened
   * with a stored maximize still applied, which is the exact report this rule
   * came from. Resolving here instead means the second call's `mapPage = true`
   * is enough to correct the first, with no ordering to get right.
   */
  private get mode(): WorkspaceMode {
    if (this.chosen !== null) return this.chosen;
    if (this.mapPage) return 'docked-right';
    return this.stored ?? 'windowed';
  }
}

/**
 * The Leads switcher moves between a table, a board and an analysis OF THE SAME
 * LEADS. They are three views of one screen, so they hold one layout
 * preference between them.
 *
 * Kept apart, they each stored their own, and the result was reported as three
 * different pages: docking the table put the card beside a live map, then
 * pressing Board handed back a floating card over a dimmed one, because Board
 * had no stored answer and fell to the windowed default. The switcher says
 * these are one screen; the layout has to agree.
 *
 * A lead opened from any of them belongs to the same key: the detail is a panel
 * inside that same card, so following a lead in must not re-mode the card
 * around it.
 */
const LEADS_COLLECTION_VIEWS = new Set(['table', 'board', 'analysis']);

/** Stable route identity for layout persistence; record identity is irrelevant. */
export function workspaceLayoutKey(url: string): string {
  const path = (url || '').split('?')[0].split('#')[0];
  const segments = path.split('/').filter(Boolean).map(segment =>
    /^\d+$/.test(segment) || isGuid(segment) ? ':id' : segment,
  );
  if (segments.length === 3 && segments[2] === 'duplicates'
      && ['leads', 'customers', 'rolloff-hires'].includes(segments[0])
      && ['table', 'board'].includes(segments[1])) segments.pop();
  // Campaign New/View replace the retained collection in the same Workspace.
  if (segments[0] === 'campaigns' && (segments.length === 1
      || segments.length === 2 && ['new', ':id'].includes(segments[1]))) return 'campaigns';
  if (segments[0] === 'leads' && LEADS_COLLECTION_VIEWS.has(segments[1])) {
    return 'leads';
  }
  return segments.length ? segments.join('/') : 'map';
}

function isGuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
    .test(value);
}

function storageKey(routeKey: string): string {
  return `${WORKSPACE_MODE_PREFIX}${routeKey}`;
}

function readMode(storage: LocalStorageService, routeKey: string): WorkspaceMode | null {
  if (!routeKey) return null;
  try {
    const stored = storage.getItem(storageKey(routeKey));
    return stored === 'maximized' || stored === 'docked-right' || stored === 'windowed'
      ? stored
      : null;
  } catch {
    return null;
  }
}

function writeMode(storage: LocalStorageService, routeKey: string, mode: WorkspaceMode): void {
  if (!routeKey) return;
  try {
    storage.setItem(storageKey(routeKey), mode);
  } catch {
    // The in-memory preference remains usable when storage is unavailable.
  }
}
